import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text, LockVeil } from '../../../../components';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { ACQ } from '../../../../theme/tokens';
import { useAcquisitionChannelGate } from '../../../../hooks/useAcquisitionChannelGate';
import type { SelfManagedChannel } from '../../../../mock-data/types';
import { Card, SecTitle, Grid, Item, Top, TopLabel, Bottom, Count, CountNum, CountLabel, Chev } from './SelfManaged.styles';

/** Where the FTUX's affiliates piece lights the way to Affiliates, on its live card only (inert outside an FTUX step). */
const AFFILIATES_TARGET: FtuxTargetId = 'acq-affiliates';

export type SelfManagedProps = {
  channels: SelfManagedChannel[];
  /** Open a self-managed channel — id read from `event.currentTarget.dataset.id`. */
  onOpen: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

/** Self-managed (non-Lumio) channels — Affiliates / Influencers (source 6466-33287). */
export const SelfManaged = ({ channels, onOpen }: SelfManagedProps): React.ReactElement => {
  const { t } = useTranslation();
  // Reactive gate: re-renders these cards when a self-managed channel is unlocked/locked
  // at runtime (the `show_in_progress` reveal flag). See `config/acquisitionChannels`.
  const { isLocked } = useAcquisitionChannelGate();
  // Channel names are a fixed UI vocabulary (the counts are the per-brand data),
  // arriving here as dynamic props from the data source — translate at the render
  // site over the known set; an unrecognised label renders as-is.
  const channelLabel = (label: string): string => {
    const labels: Record<string, string> = {
      Affiliates: t('Affiliates'),
      Influencers: t('Influencers'),
    };
    return labels[label] ?? label;
  };
  return (
    <Card>
      <SecTitle>
        <Text text="Self-managed Channels" tag="span" />
      </SecTitle>
      <Grid>
        {channels.map((channel) => {
          // Gated (release feature flag): inert and frosted under a padlock instead of
          // drilling into its page. Live channels open as before.
          const locked = isLocked(channel.id);
          const label = channelLabel(channel.label);
          return (
            <Item
              key={channel.id}
              data-id={channel.id}
              data-ftux={channel.id === 'affiliates' && !locked ? AFFILIATES_TARGET : undefined}
              $locked={locked}
              role={locked ? undefined : 'button'}
              tabIndex={locked ? -1 : 0}
              aria-disabled={locked || undefined}
              aria-label={locked ? undefined : t('Open {{name}}', { name: label })}
              onClick={locked ? undefined : onOpen}
              onKeyDown={
                locked
                  ? undefined
                  : (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpen(event);
                      }
                    }
              }
            >
              <Top>
                <Icon name={channel.icon} size={24} color={ACQ.t200} />
                <TopLabel text={label} tag="span" />
              </Top>
              <Bottom>
                <Count>
                  <CountNum text={typeof channel.count === 'number' ? String(channel.count) : '–'} tag="span" />
                  <CountLabel text={label} tag="span" />
                </Count>
                <Chev className="chev">
                  <Icon name="arrow-right-circle" size={24} color={ACQ.t50} />
                </Chev>
              </Bottom>
              {/* Match the live card's header (Icon 24 · Label 14px · gap 10 · padding 10/20) so
                  the veil overlays it, with the padlock bottom-right where the chevron sits. */}
              {locked ? (
                <LockVeil name={label} icon={channel.icon} iconSize={24} fontSize={14} headerGap={10} pad="15px 20px" lockCorner="bottom-right" lockSize={24} radius={16} />
              ) : null}
            </Item>
          );
        })}
      </Grid>
    </Card>
  );
};

export default SelfManaged;
